import Raven from 'raven-js';
import { useCallback, useEffect } from 'react';
import { useLocation, useParams } from 'quartz-routing/react-router';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import FormattedMessage from 'I18n/components/FormattedMessage';
import AwardVisibilityToggleContainer from '../containers/Achievements/AwardVisibilityToggleContainer';
import { EmbedAward, useAchievementStatus, useMaybeCreateOrUpdateAchievement } from '../features/achievement';
import { SendToUpwork } from '../features/upwork';
import { getAchievementPreviewPath, getTrackExamPageUrl } from '../utils/RoutingUtils';
import AwardShareHeader from '../features/achievement/components/AwardShareHeader';
import SocialShareButtons from '../features/achievement/components/SocialShareButtons';
import UsageTracker from '../utils/UsageTracker';
import { cleanTrackingProps } from '../utils/awardTrackingUtils';
import { NavMarker } from 'react-rhumb';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { useTrackProgress, useTrackProgressById } from '../features/userProgress';
import { View } from 'foundations-components/transitional/components/View';
import { EditorPage } from 'foundations-components/transitional/components/EditorPage';
import { Flex } from 'foundations-components/components/Flex';
import styled, { css } from 'foundations-theming/utils';
import { H1 } from 'foundations-components/transitional/components/H1';
import ImageLoader from './ImageLoader';
import I18n from 'I18n';
// @ts-expect-error image
import certBackgroundShapeUrl from 'bender-url!../../images/blob.svg';
import { Text } from 'foundations-components/components/Text';
import { useMediaQuery, mediaQuery } from 'academy-common-lib/utils/Styling';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CertificateBackground = styled.div.withConfig({
  displayName: "AwardShareControls__CertificateBackground"
})(["padding:", ";background-image:url(", ");background-position:top;background-repeat:no-repeat;background-size:contain;width:100%;", ""], ({
  theme
}) => theme.space['1000'], ({
  src
}) => src, mediaQuery('notMobile', css(["padding:", ";"], ({
  theme
}) => theme.space['600'])));
const GridContainer = styled.div.withConfig({
  displayName: "AwardShareControls__GridContainer"
})(["display:grid;grid-template-columns:1fr;grid-template-areas:'heading' 'consent' 'preview';", ""], mediaQuery('notMobile', css(["grid-template-columns:2fr 1fr;grid-template-areas:'heading preview' 'consent preview';column-gap:", ";"], ({
  theme
}) => theme.space['1300'])));
const Heading = styled.div.withConfig({
  displayName: "AwardShareControls__Heading"
})(["grid-area:heading;display:flex;align-items:end;"]);
const ShareConsent = styled.div.withConfig({
  displayName: "AwardShareControls__ShareConsent"
})(["grid-area:consent;"]);
const Preview = styled.div.withConfig({
  displayName: "AwardShareControls__Preview"
})(["grid-area:preview;"]);
const AwardShareControls = () => {
  const {
    isMobile,
    isLaptop
  } = useMediaQuery();
  const location = useLocation();
  const {
    trackId: trackIdParam
  } = useParams();
  const trackId = Number(trackIdParam);
  const {
    isDisabled
  } = useAchievementStatus(trackId);

  // Determine back navigation path, avoiding award modals to prevent navigation loops
  const getBackNavigationPath = useCallback(() => {
    var _location$state;
    const originalPath = (_location$state = location.state) === null || _location$state === void 0 || (_location$state = _location$state.backFromAwardShare) === null || _location$state === void 0 ? void 0 : _location$state.pathname;

    // exam award modal → exam page (prevents reopening the award modal they just shared from)
    if (originalPath !== null && originalPath !== void 0 && originalPath.includes('/exam/award/')) return getTrackExamPageUrl(trackIdParam);

    // fallback to original path or exam page
    return originalPath !== null && originalPath !== void 0 ? originalPath : getTrackExamPageUrl(trackIdParam);
  }, [location.state, trackIdParam]);
  const {
    trackProgressByIdLoading
  } = useTrackProgressById(trackId);
  const {
    trackProgress,
    trackProgressLoading
  } = useTrackProgress(trackId);
  const award = trackProgress === null || trackProgress === void 0 ? void 0 : trackProgress.award;
  const {
    track,
    trackLoading,
    achievementsLoading,
    achievementsError,
    isAwardInvalid
  } = useMaybeCreateOrUpdateAchievement(trackId);
  const anyLoading = achievementsLoading || trackLoading || trackProgressByIdLoading || trackProgressLoading;
  const previewPath = getAchievementPreviewPath(trackId);
  useEffect(() => {
    if (track) {
      UsageTracker.trackStandalone('Award Interaction', cleanTrackingProps({
        action: 'view-sharing-controls',
        type: 'certification',
        name: track.name,
        trackId: trackId.toString(),
        trackName: track.name,
        view: 'sharing-controls'
      }));
    }
  }, [track, trackId]);
  const handleCopyAwardCode = useCallback(() => {
    var _track$name;
    UsageTracker.trackStandalone('Award Interaction', cleanTrackingProps({
      action: 'copy-embed-code',
      type: 'certification',
      name: (_track$name = track === null || track === void 0 ? void 0 : track.name) !== null && _track$name !== void 0 ? _track$name : '',
      trackId: trackId.toString(),
      trackName: track === null || track === void 0 ? void 0 : track.name,
      view: 'sharing-controls'
    }));
  }, [track, trackId]);
  const handleImageError = ({
    currentTarget
  }) => {
    Raven.captureMessage(`Achievements: error loading cert image`, {
      extra: {
        src: currentTarget.src
      }
    });
  };
  if (anyLoading) {
    return /*#__PURE__*/_jsx(LoadingOverlay, {
      "data-test-id": "loading-spinner"
    });
  }
  if (isAwardInvalid || !award || !track) {
    return null;
  }
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "AWARD_SHARE_CONTROLS_LOADED",
    children: /*#__PURE__*/_jsx(EditorPage, {
      "data-test-id": "award-share-controls",
      headerComponent: /*#__PURE__*/_jsx(AwardShareHeader, {
        backLinkHref: getBackNavigationPath()
      }),
      inset: "0",
      position: "absolute",
      zIndex: 5,
      children: /*#__PURE__*/_jsx(Flex, {
        justifyContent: "center",
        children: /*#__PURE__*/_jsxs(View, {
          marginBlock: isMobile ? '0' : '1000',
          marginInline: isLaptop ? '0' : '1100',
          maxInlineSize: "953px",
          children: [/*#__PURE__*/_jsxs(GridContainer, {
            children: [/*#__PURE__*/_jsx(Heading, {
              children: /*#__PURE__*/_jsx(H1, {
                marginBlockEnd: isMobile ? '600' : '1100',
                typography: isMobile ? 'heading-400' : undefined,
                children: track === null || track === void 0 ? void 0 : track.name
              })
            }), /*#__PURE__*/_jsxs(ShareConsent, {
              children: [/*#__PURE__*/_jsx(H2, {
                typography: "heading-300",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "learningCenter.awardShareControls.shareHeading"
                })
              }), /*#__PURE__*/_jsxs(Flex, {
                gap: "500",
                children: [/*#__PURE__*/_jsxs(View, {
                  children: [/*#__PURE__*/_jsx(Text, {
                    paddingBlockEnd: "100",
                    typography: "heading-50",
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "learningCenter.awardShareControls.consentHeading"
                    })
                  }), /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.awardShareControls.sharingConsent"
                  })]
                }), /*#__PURE__*/_jsx(Flex, {
                  alignSelf: "center",
                  children: /*#__PURE__*/_jsx(AwardVisibilityToggleContainer, {
                    award: award,
                    disabledTooltip: "learningCenter.awardShareControls.disabledToggleTooltip",
                    isFetchingAchievements: achievementsLoading,
                    trackId: trackId
                  })
                })]
              })]
            }), /*#__PURE__*/_jsx(Preview, {
              children: /*#__PURE__*/_jsxs(Flex, {
                alignItems: "center",
                flexDirection: "column",
                marginBlockStart: isMobile ? '800' : '0',
                marginInline: isMobile ? '400' : '0',
                children: [/*#__PURE__*/_jsx(CertificateBackground, {
                  src: certBackgroundShapeUrl,
                  children: /*#__PURE__*/_jsx(ImageLoader, {
                    alt: I18n.text('learningCenter.certification.altText'),
                    errorComponent: /*#__PURE__*/_jsx(TFlex, {
                      alignItems: "center",
                      blockSize: "100%",
                      justifyContent: "center",
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "learningCenter.certification.errors.imgLoadingError"
                      })
                    }),
                    onError: handleImageError,
                    src: award === null || award === void 0 ? void 0 : award.certificate
                  })
                }), !achievementsLoading && /*#__PURE__*/_jsx(Tooltip, {
                  title: isDisabled ? /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.awardShareControls.disabledTooltip"
                  }) : undefined,
                  children: /*#__PURE__*/_jsx(UIRouterLink, {
                    "data-test-id": "open-achievement-preview",
                    disabled: !!achievementsError || isDisabled,
                    to: {
                      pathname: previewPath,
                      state: location.state
                    },
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "learningCenter.awardShareControls.previewCert"
                    })
                  })
                })]
              })
            })]
          }), /*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            marginBlockStart: "600",
            children: [/*#__PURE__*/_jsx(Text, {
              typography: "heading-50",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.awardShareControls.selectSharingOption"
              })
            }), /*#__PURE__*/_jsx(Text, {
              typography: isMobile ? 'detail-100' : undefined,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.awardShareControls.consentMessage"
              })
            }), /*#__PURE__*/_jsx(Flex, {
              justifyContent: "center",
              marginBlockStart: "300",
              children: /*#__PURE__*/_jsx(SocialShareButtons, {
                award: award,
                language: track.language,
                trackId: trackId,
                trackName: track.name
              })
            })]
          }), /*#__PURE__*/_jsxs(View, {
            as: "section",
            paddingBlockStart: "1000",
            children: [/*#__PURE__*/_jsx(EmbedAward, {
              language: track.language,
              onCopyAwardCode: handleCopyAwardCode,
              trackId: trackId
            }), track && track.upworkCode && /*#__PURE__*/_jsx(Flex, {
              alignItems: "center",
              justifyContent: "center",
              margin: "auto",
              marginBlockStart: "600",
              children: /*#__PURE__*/_jsx(SendToUpwork, {
                trackId: trackId,
                trackName: track.name
              })
            })]
          })]
        })
      })
    })
  });
};
export default AwardShareControls;